import { PlanReviewer, ReviewerStatus } from "@/Utils/Plan/PlanSubmisionsTypes";
import {
  Avatar,
  Badge,
  BadgeProps,
  Flex,
  IconProps,
  calculateUserColor
} from "@theorgchartdev/storybook";
import { useMemo } from "react";

type ApproversRendererProps = {
  approvers: PlanReviewer[];
  onClick?: () => void;
  dataTestIdPrefix?: string;
};

const getApproverInitials = (approver: PlanReviewer) => {
  const names = approver.displayName?.split(" ") ?? [approver.username];
  const initials = names.map(name => name.charAt(0).toUpperCase()).join("");
  return initials;
};

const ApproversRenderer = ({ approvers, onClick, dataTestIdPrefix }: ApproversRendererProps) => {
  return (
    <Flex gap="8px" className="ocn-plan-explorer-approvers-cell">
      {approvers.map(approver => (
        <ApproverAvatar
          key={approver.username}
          approver={approver}
          onClick={onClick}
          dataTestIdPrefix={dataTestIdPrefix}
        />
      ))}
    </Flex>
  );
};

const ApproverAvatar = ({
  approver,
  onClick,
  dataTestIdPrefix
}: {
  approver: PlanReviewer;
  onClick?: () => void;
  dataTestIdPrefix?: string;
}) => {
  const approverInitials = useMemo(() => getApproverInitials(approver), [approver]);

  const avatarColor = useMemo(() => calculateUserColor(approverInitials), [approverInitials]);

  const badgeProps: { color: BadgeProps["color"]; iconProps: IconProps } = useMemo(() => {
    switch (approver.status) {
      case ReviewerStatus.Approved:
        return {
          color: "success",
          iconProps: {
            name: "checkbox",
            size: "xsmall"
          } as IconProps
        };
      case ReviewerStatus.Rejected:
        return {
          color: "error",
          iconProps: {
            name: "xmark",
            size: "xsmall"
          } as IconProps
        };
      case ReviewerStatus.Pending:
      default:
        return {
          color: "secondary",
          iconProps: {
            name: "ellipsis",
            size: "xsmall"
          } as IconProps
        };
    }
  }, [approver.status]);

  const avatarTestId = dataTestIdPrefix
    ? `${dataTestIdPrefix}-approver-avatar-${approver.username}`
    : "plan-explorer-approver-avatar";

  return (
    <Badge pin="bottom-right" {...badgeProps}>
      <Avatar
        onClick={onClick}
        text={approverInitials}
        color={avatarColor}
        isTriggerButton
        data-testid={avatarTestId}
      />
    </Badge>
  );
};

export default ApproversRenderer;
